// eslint-disable-next-line venom/no-venom-api-calls
import { EdmundsAPI, VenomApi } from 'client/data/api/api-client';
import { set } from 'lodash-es';
import { ClientConfig } from 'client/configuration';
/* Utils */
import { handleResponse } from 'site-modules/financing/utils/pre-qualify';
import { logger } from 'client/utils/logger/logger';
import { getDecryptedFields } from 'site-modules/financing/utils/encrypting-utils';
import { getTaxesFeesApiUrl } from 'site-modules/shared/utils/financing/api';
/* Constants */
import { ERROR_STATUS, FINANCING_RESPONSE_TYPES } from 'site-modules/shared/constants/financing/financing';
import { PREQUALIFY_API } from 'site-modules/financing/constants/financing';
import { MOCK_PREQUALIFY_ID } from 'site-modules/shared/constants/financing/mock-financing-data';

export async function offersPolling({ data, options, apiMetrics, handlers }) {
  const apiOptions = {
    method: 'POST',
    retries: 0,
    headers: {
      'content-type': 'application/json',
    },
    showAPIError: true,
    body: JSON.stringify({ data, options, apiMetrics }),
  };
  const { additionalData, paymentData } = data || {};
  const { isMatchingVIN, isPreVinFlow, debugFinancingAPI } = additionalData || {};
  const commonData = { handlers, additionalData, paymentData, type: FINANCING_RESPONSE_TYPES.OFFERS_POLLING };

  // eslint-disable-next-line venom/no-venom-api-calls
  VenomApi.fetchJson(`/financing/offers-polling/`, apiOptions)
    .then(response => {
      if (response?.status) {
        handleResponse({
          ...response,
          ...commonData,
          options,
        });
      } else if (isMatchingVIN && !isPreVinFlow) {
        // eslint-disable-next-line venom/no-venom-api-calls
        VenomApi.fetchJson(`/financing/vehicle-offers/`, {
          ...apiOptions,
          body: JSON.stringify({
            data: { paymentData, offerData: { ...response?.response }, debugFinancingAPI },
            options,
            apiMetrics,
          }),
        })
          .then(res => {
            handleResponse({
              ...res,
              ...commonData,
              options,
              status: ERROR_STATUS.APPROVED,
            });
          })
          .catch(e => {
            if (e?.custom) {
              handleResponse({
                ...e?.custom,
                ...commonData,
              });
            } else {
              logger.error(
                e,
                `Error while calling ${ClientConfig.get('carBuying').capOneAPI.path}${PREQUALIFY_API.PRICING} API`
              );
            }
          });
      } else {
        handleResponse({
          ...response,
          ...response?.response,
          ...commonData,
          options,
          status: ERROR_STATUS.APPROVED,
        });
      }
    })
    .catch(e => {
      if (e?.custom) {
        handleResponse({
          ...e?.custom,
          ...commonData,
        });
      } else {
        logger.error(
          e,
          `Error while calling ${ClientConfig.get('carBuying').capOneAPI.path}${PREQUALIFY_API.OFFERS_POLLING} API`
        );
      }
    });
}

export async function prequalifySearch({ data, options, apiMetrics, handlers }) {
  const apiOptions = {
    method: 'POST',
    retries: 0,
    headers: {
      'content-type': 'application/json',
    },
    showAPIError: true,
    body: JSON.stringify({ data, options, apiMetrics }),
  };
  const { additionalData, paymentData } = data;
  const { isMatchingVIN, isPreVinFlow } = additionalData || {};
  const commonData = { handlers, additionalData, paymentData, type: FINANCING_RESPONSE_TYPES.PREQUALIFY_SEARCH };

  // eslint-disable-next-line venom/no-venom-api-calls
  VenomApi.fetchJson(`/financing/prequalify-search/`, apiOptions)
    .then(response => {
      if (response?.status) {
        handleResponse({
          ...response,
          ...commonData,
          options,
        });
      } else if (isMatchingVIN && !isPreVinFlow) {
        // eslint-disable-next-line venom/no-venom-api-calls
        VenomApi.fetchJson(`/financing/vehicle-offers/`, {
          ...apiOptions,
          body: JSON.stringify({
            data: { paymentData, offerData: { ...response?.response?.applications?.[0] } },
            options,
            apiMetrics,
          }),
        })
          .then(res => {
            handleResponse({
              ...res,
              ...commonData,
              options,
              status: ERROR_STATUS.APPROVED,
            });
          })
          .catch(e => {
            if (e?.custom) {
              handleResponse({
                ...e?.custom,
                ...commonData,
              });
            } else {
              logger.error(
                e,
                `Error while calling ${ClientConfig.get('carBuying').capOneAPI.path}${PREQUALIFY_API.PRICING} API`
              );
            }
          });
      } else {
        handleResponse({
          ...response,
          ...commonData,
          options,
          status: ERROR_STATUS.APPROVED,
        });
      }
    })
    .catch(e => {
      if (e?.custom) {
        handleResponse({
          ...e?.custom,
          ...commonData,
        });
      } else {
        logger.error(
          e,
          `Error while calling ${ClientConfig.get('carBuying').capOneAPI.path}${PREQUALIFY_API.PREQUALIFY} API`
        );
      }
    });
}

export async function postFinancingOffers({ data, options, apiMetrics, handlers }) {
  const {
    prequalificationData,
    paymentData: { calculatorSelections, vehicle },
    additionalData,
  } = data;
  const decryptedPrequalifyData = additionalData?.isE2E
    ? { id: MOCK_PREQUALIFY_ID }
    : await getDecryptedFields(JSON.parse(prequalificationData));
  const { zipCode, displayPrice, tradeIn = 0, tradeInOwedAmount = 0 } = calculatorSelections;
  const vehicleDisplayPrice = vehicle?.prices?.advertisedPrice ?? 0;
  const marketValue = vehicle?.computedDisplayInfo?.priceValidation?.listPriceEstimate ?? vehicleDisplayPrice;
  const styleId = vehicle?.vehicleInfo?.styleInfo?.styleId;
  const docFee = vehicle?.prices?.docFee ?? 0;
  const commonData = { type: FINANCING_RESPONSE_TYPES.UPDATE, additionalData, handlers };

  let taxesAndFees = {};

  if (zipCode && styleId) {
    try {
      taxesAndFees = await EdmundsAPI.fetchJson(
        getTaxesFeesApiUrl({
          zipCode,
          styleId,
          tradeIn: tradeIn - tradeInOwedAmount,
          salesPrice: displayPrice,
          marketValue,
        })
      );
      set(taxesAndFees, 'fees.docFee', Number.parseFloat(docFee));
      set(taxesAndFees, 'zipCode', zipCode);
    } catch (e) {
      logger.error(e, `Not able to get taxes and fees. Error - ${e}`);
    }
  }

  const apiOptions = {
    method: 'POST',
    retries: 0,
    headers: {
      'content-type': 'application/json',
    },
    showAPIError: true,
    body: JSON.stringify({
      data: {
        ...data,
        offerData: { ...data.offerData, prequalificationApplicationId: decryptedPrequalifyData?.id },
        taxesAndFees,
      },
      options,
      apiMetrics,
    }),
  };

  // eslint-disable-next-line venom/no-venom-api-calls
  VenomApi.fetchJson(`/financing/vehicle-offers/`, apiOptions)
    .then(res => {
      const offers =
        zipCode && styleId
          ? res?.offers?.map(offer => set(offer, 'vehicleStructure.taxesAndFees', taxesAndFees))
          : res?.offers;

      handleResponse({
        ...res,
        ...commonData,
        offers,
        options,
        paymentData: data?.paymentData,
        status: ERROR_STATUS.APPROVED,
      });
    })
    .catch(e => {
      if (e?.custom) {
        handleResponse({ ...e?.custom, ...commonData });
      } else {
        logger.error(
          e,
          `Error while calling ${ClientConfig.get('carBuying').capOneAPI.path}${PREQUALIFY_API.PRICING} API`
        );
      }
    });
}
